<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :title="computedTitle"
    dialog-width="70%"
    max-height="80vh"
    min-body-height="428px"
    no-paddings-y
    no-paddings-x
    :is-valid="computedCurrentFormValid"
    :loading="loading"
    @agree="agreeHandler"
    :agree-text="computedAgree"
  >
    <template #body>
      <v-row no-gutters>
        <v-col cols="12" v-if="isFetched">
          <v-tabs v-model="currentTab" ref="tabs" class="d-flex flex-column" hide-slider color="font">
            <v-tab
              v-for="tab in tabs"
              :disabled="disabledTab === tab.value"
              :key="tab.value"
              active-class="activeTab"
              class="font--text"
            >
              {{ tab.text }}
            </v-tab>
            <v-tabs-items v-model="currentTab" class="fill-height">
              <v-tab-item style="height: inherit">
                <v-form ref="formWms" v-model="isValidWms" class="px-5 py-6">
                  <v-row no-gutters>
                    <v-col cols="10">
                      <main-input-dialog-row v-bind="serviceTypeRadio" :key="serviceTypeRadio.name">
                        <template #[`service_type.input`]>
                          <v-radio-group
                            v-model="service_type"
                            :disabled="isCapabilitiesFetched || isEditing || isCopying"
                          >
                            <v-radio v-for="item in types" :key="item.key" :value="item.value" :label="item.text">
                            </v-radio>
                          </v-radio-group>
                        </template>
                      </main-input-dialog-row>
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="justify-center align-center">
                    <v-col cols="10">
                      <main-input-dialog-row v-bind="inputUrl" :key="inputUrl.name" v-model="wms.url">
                      </main-input-dialog-row>
                    </v-col>
                    <v-col cols="2" class="pl-3 pb-3">
                      <template v-if="!isEditing && !isCopying">
                        <base-button
                          v-if="!isCapabilitiesFetched"
                          :disabled="!isValidWms"
                          @click="fetchCapabilities"
                          :loading="isFetchCapabilitiesLoading"
                          translation-path="menu.connect"
                          style="margin-right: 0px !important;"
                        >
                        </base-button>
                        <base-button
                          v-else
                          @click="resetWms"
                          translation-path="menu.reset"
                          style="margin-right: 0px !important;"
                        >
                        </base-button>
                      </template>
                    </v-col>
                  </v-row>
                  <main-loader style="height: 150px;" v-if="isFetchCapabilitiesLoading"> </main-loader>
                  <v-row no-gutters v-if="isCapabilitiesFetched">
                    <v-col cols="10">
                      <main-input-dialog-row
                        v-for="input in wmsFilteredInputs"
                        v-bind="input"
                        :key="input.name"
                        v-model="wms[input.name]"
                      >
                        <template #[`service_layers_names.input`]="{ rules, dataType }">
                          <v-col cols="6">
                            <data-input
                              :menu-props="{ auto: false, offsetY: true }"
                              v-model="mainLayerName"
                              :items="layers"
                              clearable
                              item-text="Title"
                              :item-value="serviceLayerIdentifierKey"
                              :rules="rules"
                              :no-data-text="$i18n.t('admin.noLayers')"
                              :data-type="dataType"
                            >
                            </data-input>
                          </v-col>
                        </template>
                        <template #[`additionalServiceLayers.input`]="{ dataType }">
                          <v-col cols="6">
                            <data-input
                              :data-type="dataType"
                              :menu-props="{ offsetY: true }"
                              item-text="Title"
                              :item-value="serviceLayerIdentifierKey"
                              clearable
                              multiple
                              v-model="wms.service_layers_names"
                              :items="filteredLayers"
                              :no-data-text="$i18n.t('admin.noLayers')"
                              :auto-convert-select="false"
                            >
                              <template #selection="{ item, index }">
                                <v-tooltip v-if="index === 0" bottom color="font">
                                  <template #activator="{ on }">
                                    <v-chip v-on="on" x-small>
                                      <div class="text-truncate">{{ item.Title }}</div>
                                    </v-chip>
                                  </template>
                                  {{ item.Title }}
                                </v-tooltip>
                                <div
                                  v-if="index === 1"
                                  class="grey--text caption"
                                  style="white-space: nowrap !important;"
                                >
                                  {{ otherLayersText }}
                                </div>
                              </template>
                            </data-input>
                          </v-col>
                        </template>
                      </main-input-dialog-row>
                    </v-col>
                  </v-row>
                </v-form>
              </v-tab-item>
              <v-tab-item style="height: inherit">
                <v-row no-gutters>
                  <v-col cols="10">
                    <v-form ref="formXyz" v-model="isValidXyz" class="px-5 py-6">
                      <main-input-dialog-row
                        v-for="input in inputsXyz"
                        v-bind="input"
                        :key="input.name"
                        v-model="xyz[input.name]"
                      >
                      </main-input-dialog-row>
                    </v-form>
                  </v-col>
                </v-row>
              </v-tab-item>
            </v-tabs-items>
          </v-tabs>
          <layer-adding-permissions
            type="service_layer"
            class="px-5"
            :value="permissions"
            @input="onInput"
            @mainInput="onMainInput"
          ></layer-adding-permissions>
        </v-col>
        <main-loader v-else></main-loader>
      </v-row>
    </template>
  </main-large-dialog>
</template>

<script>
import { call, get } from 'vuex-pathify';

import { WMSCapabilities, WMTSCapabilities } from 'ol/format';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import { transformExtent } from 'ol/proj';

import MainInputDialogRow from '@/components/MainInputDialogRow';
import LayerAddingPermissions from '@/components/LayerAddingPermissions';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';

import installCrs from '@/mixins/map/crs';

export default {
  name: 'ServiceLayerAddingDialog',
  mixins: [installCrs],
  components: {
    MainLargeDialog,
    DataInput,
    MainInputDialogRow,
    MainLoader,
    LayerAddingPermissions,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    layer: {
      type: Object,
      default: null,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    isCopying: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    currentTab: undefined,
    capabilities: undefined,
    permissions: [],
    loading: false,
    isValidXyz: false,
    isValidWms: false,
    service_type: 'wms',
    isCapabilitiesFetched: false,
    isFetchCapabilitiesLoading: false,
    mainLayerName: undefined,
    isFetched: false,
    wms: {
      url: undefined,
      service_layers_names: [],
      name: undefined,
    },
    xyz: {
      url: undefined,
      maxZoom: undefined,
      minZoom: undefined,
      name: undefined,
    },
    layers: [],
    tabs: [
      {
        text: 'WMS/WMTS',
        value: 'wms',
      },
      {
        text: 'XYZ',
        value: 'xyz',
      },
    ],
    types: [
      {
        text: 'WMS',
        value: 'wms',
      },
      {
        text: 'WMTS',
        value: 'wmts',
      },
    ],
    serviceTypeRadio: {
      name: 'service_type',
      translationPath: 'dialog.body.serviceType',
      align: 'baseline',
      clearable: true,
      'validate-on-blur': true,
      isCustom: true,
      rules: [v => !!v || 'rules.required'],
    },
    isUrlChangable: true,
    disabledTab: undefined,
  }),
  computed: {
    serviceLayerNameMaxLength: get('admin/settingsValues@service_layer_name_max_length'),
    coordinatesEpsgs: get('map/coordinatesEpsgs'),
    coordinatesEpsgsNames() {
      return Object.keys(this.coordinatesEpsgs);
    },
    computedTitle() {
      return this.isEditing
        ? this.$i18n.t('dialog.title.manageLayer')
        : this.isCopying
        ? this.$i18n.t('dialog.title.copyLayer', { layer_name: this.layer?.name })
        : this.$i18n.t('dialog.title.addLayer');
    },
    computedAgree() {
      return this.isEditing ? 'dialog.agreeSave' : this.isCopying ? 'dialog.agreeCreate' : 'dialog.agreeAdd';
    },
    groups: get('users/groups'),
    inputUrl() {
      return {
        name: 'url',
        translationPath: 'dialog.body.serviceAddress',
        align: 'baseline',
        hint: 'dialog.body.pasteServiceAddress',
        clearable: !this.isCapabilitiesFetched,
        disabled: this.isCapabilitiesFetched || this.isEditing || this.isCopying,
        'validate-on-blur': true,
        rules: [v => !!v || 'rules.required', v => this.$_isValidUrl(v) || 'rules.notValidUrl'],
        cols: '12',
        dataType: {
          name: 'text',
        },
      };
    },
    wmsFilteredInputs() {
      if (this.hasOneLayer) {
        return this.inputsWms.filter(input => input.name !== 'additionalServiceLayers');
      }
      return this.inputsWms;
    },
    inputsWms() {
      return [
        {
          name: 'service_layers_names',
          'validate-on-blur': true,
          translationPath: 'dialog.body.serviceLayers',
          clearable: true,
          align: 'baseline',
          isCustom: true,
          rules: [v => !!v || this.$i18n.t('rules.required')],
          dataType: {
            name: 'select',
          },
        },
        {
          disabled: true,
          name: 'additionalServiceLayers',
          subtitle: 'dialog.body.checkAdditionalServiceLayers',
          'validate-on-blur': true,
          isCustom: true,
          align: 'baseline',
          clearable: true,
          translationPath: 'dialog.body.additionalServiceLayers',
          dataType: {
            name: 'select',
          },
        },
        {
          name: 'name',
          align: 'baseline',
          cols: '12',
          hint: 'dialog.body.typeLayerDisplayName',
          translationPath: 'dialog.body.layerDisplayName',
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.serviceLayerNameMaxLength,
          },
        },
      ];
    },
    inputsXyz() {
      return [
        {
          name: 'url',
          translationPath: 'dialog.body.serviceAddress',
          hint: 'dialog.body.pasteServiceAddress',
          clearable: true,
          cols: 12,
          align: 'baseline',
          'validate-on-blur': true,
          rules: [v => !!v || 'rules.required', v => this.$_isValidUrl(v) || 'rules.notValidUrl'],
          dataType: {
            name: 'text',
          },
          disabled: this.isEditing || this.isCopying,
        },
        {
          name: 'name',
          hint: 'dialog.body.typeLayerDisplayName',
          clearable: true,
          cols: 12,
          'validate-on-blur': true,
          align: 'baseline',
          translationPath: 'dialog.body.layerDisplayName',
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.serviceLayerNameMaxLength,
          },
        },
        {
          name: 'minZoom',
          clearable: true,
          cols: 12,
          align: 'baseline',
          'validate-on-blur': true,
          translationPath: 'dialog.body.minZoom',
          dataType: {
            name: 'integer',
          },
        },
        {
          clearable: true,
          'validate-on-blur': true,
          align: 'baseline',
          cols: 12,
          name: 'maxZoom',
          translationPath: 'dialog.body.maxZoom',
          dataType: {
            name: 'integer',
          },
        },
      ];
    },
    isWms() {
      return this.service_type === 'wms';
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    computedCurrentFormValid() {
      return this.currentTab === 0 ? this.isValidWms && this.isCapabilitiesFetched : this.isValidXyz;
    },
    hasOneLayer() {
      return this.layers.length === 1;
    },
    serviceLayerIdentifierKey() {
      return this.isWms ? 'Name' : 'Identifier';
    },
    filteredLayers() {
      return this.mainLayerName
        ? this.layers.filter(layer => layer[this.serviceLayerIdentifierKey] !== this.mainLayerName)
        : this.layers;
    },
    selectedMainLayer() {
      return this.layers?.find(layer => layer[this.serviceLayerIdentifierKey] === this.mainLayerName);
    },
    otherLayersText() {
      const currentLength = this.mainLayerName ? 2 : 1;
      const otherLayersLength = this.wms.service_layers_names.length - currentLength;
      return `(+${this.$i18n.tc('admin.other', otherLayersLength)})`;
    },
  },
  watch: {
    'wms.url'(nV) {
      this.onUrlInput(nV);
    },
    computedIsVisible: {
      immediate: true,
      handler(nV) {
        if (nV) {
          this.init();
        } else {
          this.onClose();
        }
      },
    },
  },
  methods: {
    getGroups: call('users/getGroups'),
    getLayerPermissions: call('layers/getLayerPermissions'),
    editLayerAction: call('layers/editLayer'),
    addLayerAction: call('layers/addLayer'),
    getWmsCapabilities: call('layers/getWmsCapabilities'),
    onUrlInput(value) {
      if (value) {
        try {
          const url = new URL(value);
          if (url.search) {
            const { origin, pathname } = url;
            this.wms.url = `${origin}${pathname}`;
          } else {
            this.wms.url = this.wms.url.trim();
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
    removeOldLayer(name) {
      this.wms.service_layers_names.splice(this.wms.service_layers_names.indexOf(name), 1);
    },
    getWmtsOptionsHelper(layer) {
      const options = optionsFromCapabilities(this.capabilities, {
        layer,
        matrixSet: this.$_config.defaultEpsg,
      });
      // options for defaultEpsg not available
      if (options.matrixSet.split(':')[0] !== 'EPSG') {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.crsNotSupported'),
        });
        return optionsFromCapabilities(this.capabilities, {
          layer,
          matrixSet: this.capabilities.Contents.TileMatrixSet[0].SupportedCRS,
        });
      }
      return options;
    },
    getWmtsOptions(layer) {
      const { matrixSet: CRS, tileGrid, format } = this.getWmtsOptionsHelper(layer);
      const { WGS84BoundingBox } = this.capabilities.Contents.Layer.find(lay => lay.Identifier === layer);
      return {
        CRS,
        format,
        options: {
          resolutions: tileGrid.resolutions_,
          tileSizes: tileGrid.tileSizes_,
          matrixIds: tileGrid.matrixIds_,
          origins: tileGrid.origins_,
          extent: WGS84BoundingBox ? transformExtent(WGS84BoundingBox, 'EPSG:4326', CRS) : undefined,
          style: '',
        },
      };
    },
    onLayerChange(nV, oV) {
      if (oV && Object.keys(oV).length > 0) {
        this.removeOldLayer(oV[this.serviceLayerIdentifierKey]);
      }
      if (!nV) {
        this.wms.name = '';
      } else {
        this.wms.name = nV.Title;
        this.wms.parameters = {};
        const isLayerAlreadyAdded = this.wms.service_layers_names.includes(nV[this.serviceLayerIdentifierKey]);
        if (!isLayerAlreadyAdded) {
          this.wms.service_layers_names.push(nV[this.serviceLayerIdentifierKey]);
        }
        if (this.service_type === 'wmts') {
          this.wms.parameters = this.getWmtsOptions(nV[this.serviceLayerIdentifierKey]);
        } else if (this.service_type === 'wms') {
          const epsg = this.getWmsEpsg(nV.CRS);
          if (epsg !== this.$_config.defaultEpsg) {
            this.$store.set('snackbar/PUSH_MESSAGE!', {
              message: this.$i18n.t('snackbar.crsNotSupported'),
            });
          }
          if (epsg) {
            this.wms.parameters.CRS = epsg;
          }
        }
        this.wms.parameters.version = this.capabilities.version;
      }
    },
    onInput(e) {
      const index = this.permissions.map(permission => permission.group_id).indexOf(e.group_id);
      this.permissions.splice(index, 1, { ...e });
    },
    onMainInput(v) {
      this.permissions = this.permissions.map(permission => {
        return { ...permission, main_value: v };
      });
    },
    getPermissions() {
      return this.$_objectToArray(this.groups)
        .filter(group => !group.is_admin_group)
        .map(group => {
          return { group_id: group.id, main_value: 0 };
        });
    },
    setEditedData(type, layer) {
      this.service_type = type;
      this.currentTab = type === 'xyz' ? 1 : 0;
      this.disabledTab = type === 'xyz' ? 'wms' : 'xyz';
      const { name, url } = layer;
      if (type === 'xyz') {
        const { maxZoom, minZoom } = layer.parameters;
        this.xyz = {
          url,
          maxZoom,
          minZoom,
          name,
        };
      } else {
        const { service_layers_names } = layer;
        this.wms = {
          url,
          service_layers_names: [...service_layers_names],
          name,
        };
      }
    },
    applyWatcher() {
      this.unwatch = this.$watch('selectedMainLayer', this.onLayerChange);
    },
    async init() {
      try {
        await this.getGroups();
        if (this.isEditing || this.isCopying) {
          const { id, service_type, service_layers_names } = this.layer;
          this.setEditedData(service_type, this.layer);
          this.permissions = JSON.parse(JSON.stringify((await this.getLayerPermissions(id)).data.data));
          if (service_type !== 'xyz') {
            await this.fetchCapabilities();
            this.mainLayerName = service_layers_names[0];
          }
        } else {
          this.permissions = JSON.parse(JSON.stringify(this.getPermissions()));
          this.isUrlChangable = true;
        }
        this.applyWatcher();
        this.isFetched = true;
      } catch (error) {
        console.log(error);
      }
    },
    getCapabilitiesLayers() {
      // [this.capabilities.Capability.Layer] - https://gis-support.atlassian.net/browse/AQ30-2243 - https://mapy.geoportal.gov.pl/wss/service/PZGIK/NMT/GRID1/WMS/Hypsometry has single layer placed under Capability.Layer path
      return this.isWms
        ? this.capabilities.Capability.Layer.Layer || [this.capabilities.Capability.Layer]
        : this.capabilities.Contents.Layer;
    },
    getWmsEpsg(crses = []) {
      const epsgs = crses.filter(crs => crs.startsWith('EPSG:'));
      const epsg = this.coordinatesEpsgsNames.find(coordEpsg => epsgs.includes(coordEpsg));
      return epsg || '';
    },
    async fetchCapabilities() {
      try {
        this.isFetchCapabilitiesLoading = true;
        const { url } = this.wms;
        const r = await this.getWmsCapabilities({ url, service_type: this.service_type });
        const parser = this.isWms ? new WMSCapabilities() : new WMTSCapabilities();
        this.capabilities = parser.read(r.data);
        this.layers = this.getLayers(this.getCapabilitiesLayers());
        if (this.hasOneLayer) {
          this.mainLayerName = this.layers[0][this.serviceLayerIdentifierKey];
        }
        this.isCapabilitiesFetched = true;
        this.isFetchCapabilitiesLoading = false;
      } catch (error) {
        this.isFetchCapabilitiesLoading = false;
        console.log(error);
      }
    },
    getLayers(layers) {
      return layers.reduce((total, current) => {
        if (current.Layer) {
          return [...total, ...this.getLayers(current.Layer)];
        } else {
          return [...total, current];
        }
      }, []);
    },
    async agreeHandler() {
      if (this.currentTab === 0) {
        await (this.isEditing ? this.editWmsWmtsLayer() : this.addWmsWmtsLayer());
      } else {
        await (this.isEditing ? this.editXyzLayer() : this.addXyzLayer());
      }
    },
    async addWmsWmtsLayer() {
      const { wms, service_type, permissions } = this;
      await this.agreeModal(this.addLayerAction, {
        layer_type: 'service_layers',
        body: {
          ...wms,
          service_type,
          layer_scope: 'core',
          permissions,
        },
      });
    },
    async editWmsWmtsLayer() {
      const { wms, permissions } = this;
      await this.agreeModal(this.editLayerAction, {
        scope: 'core',
        layer_id: this.layer.id,
        layer_type: 'service_layers',
        body: {
          ...wms,
          permissions,
        },
      });
    },
    async addXyzLayer() {
      const { name, url, maxZoom, minZoom } = this.xyz;
      await this.agreeModal(this.addLayerAction, {
        layer_type: 'service_layers',
        body: {
          name,
          url,
          service_type: 'xyz',
          layer_scope: 'core',
          permissions: this.permissions,
          service_layers_names: [name],
          parameters: {
            minZoom,
            maxZoom,
          },
        },
      });
    },
    async editXyzLayer() {
      const { name, maxZoom, minZoom } = this.xyz;
      await this.agreeModal(this.editLayerAction, {
        layer_type: 'service_layers',
        layer_id: this.layer.id,
        scope: 'core',
        body: {
          name,
          permissions: this.permissions,
          service_layers_names: [name],
          parameters: {
            minZoom,
            maxZoom,
          },
        },
      });
    },
    async agreeModal(f, payload) {
      try {
        this.loading = true;
        await f(payload);
        this.loading = false;
        this.computedIsVisible = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    onClose() {
      this.resetWms();
      this.resetXyz();
      this.$emit('update:layer', undefined);
      this.currentTab = 0;
      this.disabledTab = undefined;
      this.isFetched = false;
      this.unwatch?.();
    },
    resetXyz() {
      this.xyz = {
        url: undefined,
        maxZoom: undefined,
        minZoom: undefined,
        name: undefined,
      };
      this.$refs?.formXyz?.resetValidation();
    },
    resetWms() {
      this.mainLayerName = undefined;
      this.isCapabilitiesFetched = false;
      this.wms = {
        url: undefined,
        service_layers_names: [],
        name: undefined,
      };
      this.$refs?.formWms?.resetValidation();
    },
  },
};
</script>

<style lang="scss" scoped>
.activeTab {
  background-color: rgba(207, 207, 207, 0.25);
}
::v-deep {
  .v-slide-group {
    z-index: 99;
    box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14),
      0px 1px 10px 0px rgba(0, 0, 0, 0.12) !important;
  }
}
</style>
